<template>
  <base-drawer :show="show" :title="drawerTitle" @close="onCloseRequest">
    <drawer-section title="Template details" icon="tim-icons icon-chat-33">
      <base-input
        label="Template name"
        placeholder="e.g. Booking confirmation"
        :error="fieldErrors.name"
        v-model="form.name"
      />

      <base-input label="Type" :error="fieldErrors.template_type_id">
        <el-select v-model="form.template_type_id" class="select-primary" placeholder="Choose a type">
          <el-option
            v-for="type in templateTypes"
            :key="type.id"
            class="select-primary"
            :label="type.name"
            :value="type.id"
          />
        </el-select>
      </base-input>

      <div class="sms-drawer__toggle">
        <el-switch
          :value="form.status === 'active'"
          active-color="#e14eca"
          @change="onStatusToggle"
        />
        <label class="text-white mb-0">Active</label>
      </div>
      <p class="sms-drawer__hint">
        Only active templates can be selected in an automation.
      </p>
    </drawer-section>

    <drawer-section title="Message" icon="tim-icons icon-send">
      <base-input label="Message body" :error="fieldErrors.body">
        <base-textarea
          :rows="5"
          :maxlength="1600"
          :show-word-limit="false"
          :placeholder="bodyPlaceholder"
          v-model="form.body"
        />
      </base-input>

      <div class="sms-drawer__meter" :class="{ 'sms-drawer__meter--multi': segments.segments > 1 }">
        <span>{{ segments.length }} characters</span>
        <span>&middot;</span>
        <span>{{ segments.segments }} segment{{ segments.segments === 1 ? '' : 's' }}</span>
        <span>&middot;</span>
        <span>{{ segments.encoding }}</span>
        <span v-if="segments.segments > 0">&middot; {{ segments.remaining }} left in this segment</span>
      </div>
      <p v-if="segments.encoding === 'UCS-2'" class="sms-drawer__hint">
        This message contains an emoji or special character, so it is limited to
        {{ segments.perSegment }} characters per segment instead of 160.
      </p>
      <p v-if="!bodyIncludesOptOut" class="sms-drawer__hint sms-drawer__hint--warn">
        Consider including "Reply STOP to opt out" so recipients always know how to unsubscribe.
      </p>
    </drawer-section>

    <drawer-section title="Personalization" icon="tim-icons icon-tag">
      <p class="sms-drawer__hint mb-2">
        Click a variable to add it to your message. Only variables text messages can
        fill in are listed, so nothing sends as unresolved placeholder text.
      </p>
      <smart-values-panel
        :allowed-keys="allowedVariableKeys"
        @insert-value="onInsertVariable"
      />
    </drawer-section>

    <drawer-section title="Preview" icon="tim-icons icon-mobile">
      <div class="sms-drawer__phone">
        <div class="sms-drawer__bubble">{{ previewBody || 'Your message will appear here.' }}</div>
      </div>
      <p class="sms-drawer__hint">
        Sample values shown. Real messages use the customer and booking that triggered the automation.
      </p>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="saving" @click="save">
          {{ saving ? 'Saving...' : (form.id ? 'Save changes' : 'Create template') }}
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script>
import { Select, Option, Switch } from 'element-ui';
import swal from 'sweetalert2';
import BaseTextarea from '@/components/Inputs/BaseTextarea.vue';
import SmartValuesPanel from '@/components/SmartValuesPanel.vue';
import { calculateSegments } from '@/util/smsSegments';
import { SMS_VARIABLE_KEYS, SMS_SAMPLE_VALUES } from '@/util/smsVariables';

function emptyForm() {
  return { id: null, name: '', template_type_id: '', body: '', status: 'draft' };
}

export default {
  name: 'SMSTemplateDrawer',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
    BaseTextarea,
    'smart-values-panel': SmartValuesPanel
  },
  props: {
    show: { type: Boolean, default: false },
    template: { type: Object, default: null },
    templateTypes: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
    validationErrors: { type: Array, default: () => [] }
  },
  data() {
    return {
      form: emptyForm()
    };
  },
  computed: {
    bodyPlaceholder() {
      return 'Hi {{customer.firstName}}, your booking is confirmed. Reply STOP to opt out.';
    },
    drawerTitle() {
      return this.form.id ? 'Edit SMS template' : 'New SMS template';
    },
    segments() {
      return calculateSegments(this.form.body || '');
    },
    bodyIncludesOptOut() {
      return /\bstop\b/i.test(this.form.body || '');
    },
    allowedVariableKeys() {
      return SMS_VARIABLE_KEYS;
    },
    previewBody() {
      let text = this.form.body || '';
      Object.keys(SMS_SAMPLE_VALUES).forEach((key) => {
        text = text.split(`{{${key}}}`).join(SMS_SAMPLE_VALUES[key]);
      });
      return text;
    },
    fieldErrors() {
      const map = {};
      for (const e of this.validationErrors) {
        if (e && e.field && !map[e.field]) map[e.field] = e.message || 'Invalid';
      }
      return map;
    },
    isDirty() {
      const base = this.template
        ? { id: this.template.id, name: this.template.name, template_type_id: this.template.template_type_id, body: this.template.body, status: this.template.status }
        : emptyForm();
      return JSON.stringify(base) !== JSON.stringify(this.form);
    }
  },
  watch: {
    // Seed on open so reopening the drawer never shows the previous template.
    show(isOpen) {
      if (isOpen) this.seed();
    },
    template() {
      if (this.show) this.seed();
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    if (this.show) this.seed();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    seed() {
      if (this.template) {
        this.form = {
          id: this.template.id,
          name: this.template.name || '',
          template_type_id: this.template.template_type_id || '',
          body: this.template.body || '',
          status: this.template.status || 'draft'
        };
      } else {
        const form = emptyForm();
        // Default to the first type so the required field is rarely empty.
        if (this.templateTypes.length) form.template_type_id = this.templateTypes[0].id;
        this.form = form;
      }
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.show) this.onCloseRequest();
    },
    onStatusToggle(isActive) {
      this.form.status = isActive ? 'active' : 'draft';
    },
    // The panel emits the whole catalog row; `syntax` is the token to write.
    onInsertVariable(value) {
      const token = value && value.syntax ? value.syntax : '';
      if (token) this.form.body = (this.form.body || '') + token;
    },
    onClose() {
      this.$emit('close');
    },
    // Guarded dismissal: closing with unsaved edits asks first (Cancel is explicit).
    async onCloseRequest() {
      if (!this.isDirty) return this.onClose();
      const result = await swal({
        title: 'Discard changes?',
        text: 'This template has unsaved changes.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Discard',
        cancelButtonText: 'Keep editing'
      });
      if (result.value) this.onClose();
    },
    save() {
      this.$emit('save', { ...this.form });
    }
  }
};
</script>

<style lang="scss" scoped>
/* Same rule as PassTypeDrawer: footer actions right-aligned with a 12px gap. */
.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.sms-drawer {
  &__hint {
    color: rgba(255, 255, 255, 0.65);
    font-size: 0.75rem;
    line-height: 1.45;
    margin-top: 0.35rem;

    &--warn {
      color: #ffd67e;
    }
  }

  &__toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 0.5rem;
  }

  &__meter {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.7);
    font-variant-numeric: tabular-nums;
    margin-top: 0.35rem;

    &--multi {
      color: #e14eca;
    }
  }



  &__phone {
    background: rgba(125, 125, 125, 0.1);
    border-radius: 0.5rem;
    padding: 14px;
  }

  &__bubble {
    background: #e14eca;
    color: #ffffff;
    border-radius: 14px 14px 4px 14px;
    padding: 10px 14px;
    font-size: 0.875rem;
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
    max-width: 85%;
    margin-left: auto;
  }
}
</style>

<style>
.white-content .sms-drawer__hint { color: rgba(0, 0, 0, 0.6); }
.white-content .sms-drawer__hint--warn { color: #b8860b; }
.white-content .sms-drawer__meter { color: rgba(0, 0, 0, 0.7); }
</style>
